import { DocumentVersion, FileType } from '@alucio/aws-beacon-amplify/src/models';

export const supportsDocumentSummary = (type?: DocumentVersion['type'] | null): boolean =>
  type === FileType.PDF || type === FileType.PPTX;

interface DocumentSummaryResponse {
  documentVersionId: string;
  content: {
    documentSummary?: string;
    longSummary?: string | null;
    shortSummary?: string | null;
  };
}

export const normalizeDocumentSummary = (documentSummary?: string): string => {
  if (!documentSummary) return '';

  return documentSummary
    .replace(/!\[([^\]]*)\]\([^)]*\)/g, '$1')
    .replace(/\[([^\]]+)\]\([^)]*\)/g, '$1')
    .replace(/^\s{0,3}#{1,6}\s+/gm, '')
    .replace(/^\s{0,3}(?:[-*+] |\d+\. )/gm, '')
    .replace(/^\s{0,3}>\s?/gm, '')
    .replace(/(\*\*|__|~~|`|\*|_)/g, '')
    .replace(/\s+/g, ' ')
    .trim();
};

export const getDocumentSummariesByVersion = (
  responses: DocumentSummaryResponse[],
): Record<string, string> => responses.reduce<Record<string, string>>((summaries, response) => {
  const documentSummary = response.content.shortSummary;

  if (documentSummary) {
    summaries[response.documentVersionId] = documentSummary;
  }

  return summaries;
}, {});

export const getDocumentSummariesFromVersions = (
  versions: Array<{ id: string; shortSummary?: string | null }>,
): Record<string, string> => versions.reduce<Record<string, string>>((summaries, version) => {
  if (version.shortSummary != null) {
    summaries[version.id] = version.shortSummary;
  }

  return summaries;
}, {});

export const getUncachedDocumentSummaryPageJsonKeys = (
  pageJsonKeys: Record<string, string>,
  cachedDocumentSummaries: Record<string, string | undefined>,
): Record<string, string> => Object.entries(pageJsonKeys).reduce<Record<string, string>>(
  (uncachedPageJsonKeys, [documentVersionId, pageJsonKey]) => {
    if (!(documentVersionId in cachedDocumentSummaries)) {
      uncachedPageJsonKeys[documentVersionId] = pageJsonKey;
    }

    return uncachedPageJsonKeys;
  },
  {},
);

export interface TabletDocumentRow<T> {
  document: T;
  documentSummaries: Record<string, string>;
}

export const getTabletDocumentRows = <T>(
  documents: T[],
  documentSummaries: Record<string, string>,
): TabletDocumentRow<T>[] => documents.map(document => ({ document, documentSummaries }));

export const hasTabletDocumentRowChanged = <T>(
  previous: TabletDocumentRow<T>,
  next: TabletDocumentRow<T>,
): boolean => previous.document !== next.document || previous.documentSummaries !== next.documentSummaries;
